/**
 * API Configuration
 * Why this matters: Centralizes backend URL configuration to support different deployment environments
 * (Vercel, Netlify, local development) without hardcoding URLs throughout the codebase
 */

// Get the backend URL from environment variable or use defaults
// Why this matters: Allows different deployments to point to their respective backends
const getBackendUrl = (): string => {
  // First, check if REACT_APP_API_URL is set (used by Netlify and can be used by Vercel)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback to NODE_ENV-based logic
  if (process.env.NODE_ENV === 'production') {
    // Default to Vercel backend for production if no env var is set
    return 'https://us-citrus-ai-backend.vercel.app';
  }
  
  // Local development
  return 'http://localhost:3005';
};

export const API_BASE_URL = getBackendUrl();

// Helper function to ensure URLs don't have trailing slashes
// Why this matters: Prevents double slashes in API URLs which can cause routing issues
export const buildApiUrl = (path: string): string => {
  const baseUrl = API_BASE_URL.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

// Export specific API endpoints for consistency
export const API_ENDPOINTS = {
  // Content generation endpoints
  generateContent: buildApiUrl('/api/content/generate'),
  generateMeta: buildApiUrl('/api/content/generate-meta'),
  enhanceWithCtas: buildApiUrl('/api/content/enhance-with-ctas'),
  enhanceWithSimpleCtas: buildApiUrl('/api/content/enhance-with-simple-ctas'),
  
  // Blog creator endpoints
  blogCreatorGenerateContent: buildApiUrl('/api/blog-creator/generate-content'),
  blogCreatorGenerateContentStreaming: buildApiUrl('/api/blog-creator/generate-content-streaming'),
  blogCreatorGenerateContentAsync: buildApiUrl('/api/blog-creator/generate-content-async'),
  blogCreatorJobStatus: (jobId: string) => buildApiUrl(`/api/blog-creator/job-status/${jobId}`),
  
  // Playbook endpoints
  generatePlaybook: buildApiUrl('/api/playbooks/generate-content'),
  
  // Competitor conquesting endpoints
  competitorGenerateContent: buildApiUrl('/api/competitor-conquesting/generate-content'),
  competitorGenerateContentAsync: buildApiUrl('/api/competitor-conquesting/generate-content-async'),
  competitorJobStatus: (jobId: string) => buildApiUrl(`/api/competitor-conquesting/job-status/${jobId}`),
  competitorCsvCognism: buildApiUrl('/api/competitor-conquesting/csv/cognism'),
  
  // Rewrite blog post endpoints  
  rewriteBlogPostGenerateAsync: buildApiUrl('/api/rewrite-blog-post/generate-async'),
  rewriteBlogPostJobStatus: (jobId: string) => buildApiUrl(`/api/rewrite-blog-post/job-status/${jobId}`),
  
  // Citrus tree content endpoints
  citrusTreeGenerateContentAsync: buildApiUrl('/api/citrus-tree-content/generate-async'),
  citrusTreeJobStatus: (jobId: string) => buildApiUrl(`/api/citrus-tree-content/job-status/${jobId}`),
  citrusTreeGenerateImages: buildApiUrl('/api/citrus-tree-content/generate-images'),
  citrusTreeGenerateInfographic: buildApiUrl('/api/citrus-tree-content/generate-infographic'),

  // Dr. Mani product article images (GPT-Image-2, product-aware)
  productArticlesGenerateImages: buildApiUrl('/api/product-articles/generate-images'),
  productArticlesGenerateInfographic: buildApiUrl('/api/product-articles/generate-infographic'),
  
  // PAA content generation endpoints
  paaContentGenerateAsync: buildApiUrl('/api/paa-content/generate-async'),
  paaContentJobStatus: (jobId: string) => buildApiUrl(`/api/paa-content/job-status/${jobId}`),
  
  // Topical content generation endpoints
  topicalContentGenerateAsync: buildApiUrl('/api/topical-content/generate-async'),
  topicalContentJobStatus: (jobId: string) => buildApiUrl(`/api/topical-content/job-status/${jobId}`),
  topicalContentCancel: (jobId: string) => buildApiUrl(`/api/topical-content/cancel/${jobId}`),

  // Dr. Mani article agent endpoints
  ckaaArticlesGenerateAsync: buildApiUrl('/api/ckaa-articles/generate-async'),
  ckaaArticlesJobStatus: (jobId: string) => buildApiUrl(`/api/ckaa-articles/job-status/${jobId}`),
  psbArticlesGenerateAsync: buildApiUrl('/api/psb-articles/generate-async'),
  psbArticlesJobStatus: (jobId: string) => buildApiUrl(`/api/psb-articles/job-status/${jobId}`),
  ssArticlesGenerateAsync: buildApiUrl('/api/ss-articles/generate-async'),
  ssArticlesJobStatus: (jobId: string) => buildApiUrl(`/api/ss-articles/job-status/${jobId}`),
  tofuArticlesGenerateAsync: buildApiUrl('/api/tofu-articles/generate-async'),
  tofuArticlesJobStatus: (jobId: string) => buildApiUrl(`/api/tofu-articles/job-status/${jobId}`),
  bestOfArticlesGenerateAsync: buildApiUrl('/api/best-of-articles/generate-async'),
  bestOfArticlesJobStatus: (jobId: string) => buildApiUrl(`/api/best-of-articles/job-status/${jobId}`),
  bestOfArticlesCancel: (jobId: string) => buildApiUrl(`/api/best-of-articles/cancel/${jobId}`),
  
  // Shopify publish endpoint
  publishToShopify: buildApiUrl('/api/content/publish-to-shopify'),
  
  // Sitemap endpoints
  sitemapScrape: buildApiUrl('/api/sitemap/scrape'),
  sitemapHealth: buildApiUrl('/api/sitemap/health'),
  
  // Sitemap chunked endpoints (for large sitemaps)
  sitemapParse: buildApiUrl('/api/sitemap-chunked/parse'),
  sitemapScrapeChunk: buildApiUrl('/api/sitemap-chunked/scrape-chunk'),
  sitemapSession: (sessionId: string) => buildApiUrl(`/api/sitemap-chunked/session/${sessionId}`),
  
  // Email Newsletter endpoints
  emailNewsletter: {
    // ASYNC endpoints (NO TIMEOUT - use these!)
    generateAsync: buildApiUrl('/api/email-newsletter/generate-async'),
    regenerateAsync: buildApiUrl('/api/email-newsletter/regenerate-async'),
    jobStatus: (jobId: string) => buildApiUrl(`/api/email-newsletter/job-status/${jobId}`),
    
    // Old sync endpoints (kept for backward compatibility - will timeout in production!)
    generate: buildApiUrl('/api/email-newsletter/generate'),
    regenerate: buildApiUrl('/api/email-newsletter/regenerate'),
    
    // Utility endpoints
    jobTitles: buildApiUrl('/api/email-newsletter/job-titles'),
    ctaOptions: buildApiUrl('/api/email-newsletter/cta-options'),
    validate: buildApiUrl('/api/email-newsletter/validate'),
    mcpStatus: buildApiUrl('/api/email-newsletter/mcp-status'),
    mcpReset: buildApiUrl('/api/email-newsletter/mcp-reset'),
    mcpConnect: buildApiUrl('/api/email-newsletter/mcp-connect'),
    mcpTest: buildApiUrl('/api/email-newsletter/mcp-test')
  },
  
  // Add other endpoints as needed
};

if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration:', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    API_BASE_URL,
  });

  console.log('🎭 Jokes API URL will be:', `${API_BASE_URL}/api/jokes/random`);
}
